import { useContext, useLayoutEffect } from "react";
import styled, {
  css,
  DefaultTheme,
  FlattenInterpolation,
  ThemeProps,
} from "styled-components";
import {
  Alert,
  Alerts,
  AlertsContext,
  Flex,
  FormErrorTypes,
  ModalProvider,
  SideBar,
} from "dash-ui";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import BreadcrumbNavigation from "flow-components/BreadcrumbNavigation/BreadcrumbNavigation";
import BulkActionsContainer from "containers/properties/components/BulkActionsContainer/BulkActionsContainer";
import { useDetectSticky } from "common/hooks/useDetectSticky";
import { BreadcrumbContext } from "common/contexts/breadcrumb-context";

import { isRouteActive } from "shared/config/routes";

import { IContainerProps } from "./Container.interfaces";
import { sidebarItemsList } from "./Container.constants";

const ContainerStyle = styled(Flex)`
  background-color: ${({ theme }) => theme.colors.n20};
  min-height: calc(100vh - 50px);
  box-sizing: border-box;
  max-width: calc(100% - 248px);
  @media ${({ theme }) => `${theme.devices.tablet}, ${theme.devices.mobile}`} {
    max-width: unset;
  }
`;
const BreadcrumbWrapper = styled<{ stickyBreadcrumbs: boolean } & any>(Flex)`
  background-color: inherit;

  ${({ stickyBreadcrumbs }) =>
    stickyBreadcrumbs
      ? css`
          top: 50px;
          position: sticky;
          z-index: 5;
        `
      : ""}
`;

const StickyHeader = styled(Flex)`
  top: -1px;
  position: sticky;
`;

const Content = styled(Flex)<{
  css?: FlattenInterpolation<ThemeProps<DefaultTheme>> | string;
}>`
  box-shadow: ${({ theme }) => theme.shadows.shadow1};
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.white};
  width: 100%;
  ${({ css }) => css}
`;

function Container({
  children,
  contentStyle,
  stickyBreadcrumbs = false,
  withSidebar = true,
}: IContainerProps) {
  const { t } = useTranslation("common");
  const { state } = useContext(AlertsContext);
  const { setIsSticky } = useContext(BreadcrumbContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [isSticky, ref] = useDetectSticky();

  useLayoutEffect(() => {
    if (stickyBreadcrumbs) {
      setIsSticky(isSticky as boolean);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSticky]);

  return (
    <Flex flexDirection="row" w="inherit">
      {withSidebar && (
        <SideBar
          items={sidebarItemsList.map(({ label, icon, path, disabled }) => ({
            label: t(`labels.${label}`),
            icon,
            isActive: isRouteActive(location.pathname, path),
            disabled,
            onClick: () => navigate(path),
          }))}
        />
      )}
      <ContainerStyle flex={1} p={11} pt={7} flexDirection="column" w="100%">
        <StickyHeader
          w="100%"
          py={4}
          // @ts-ignore
          ref={ref}
        />
        <BreadcrumbWrapper
          alignItems="center"
          justifyContent="space-between"
          w="100%"
          py={4}
          stickyBreadcrumbs={stickyBreadcrumbs}
        >
          <BreadcrumbNavigation />
          {state[Alerts.ChangesSaved] && (
            <Alert type={FormErrorTypes.Valid} alertType={Alerts.ChangesSaved}>
              {t("alerts.changesSaved")}
            </Alert>
          )}
        </BreadcrumbWrapper>
        <Content css={contentStyle}>{children}</Content>
        <ModalProvider>
          <BulkActionsContainer />
        </ModalProvider>
      </ContainerStyle>
    </Flex>
  );
}

export default Container;
